import { PaginationReturn } from '@common/types/service';
import { ManagementAccountCertificate } from '@common/types/user';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { useDashboardLayoutScrollElement } from '@components/layout/dashboard';
import { useLazyDownload } from '@hooks/useDownload';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { MediaItemCard } from '@mediaAssets/components/display';
import CertificateImage from '@members/assets/images/certificate.png';
import { Grid2 } from '@mui/material';
import { useLazyGetAccountCertificatesQuery } from '@services/accounts';
import { handleError } from '@utils/common';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export const MemberEarnedCertificates = () => {
    const scrollElement = useDashboardLayoutScrollElement();
    const { id: user_id } = useParams();

    const [getAccountCertificates, { isError: isErrorFetchingCertificates }] = useLazyGetAccountCertificatesQuery();
    const [downloadCertificate] = useLazyDownload();
    const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

    const loadCertificatesPage = async ({ page, pageSize }: { page: number; pageSize: number }) => {
        const response = await getAccountCertificates(
            {
                page,
                page_size: pageSize,
                user_id: Number(user_id),
            },
            true
        ).unwrap();

        return response as PaginationReturn<ManagementAccountCertificate>;
    };

    const { items, sentinelRef, hasRealItems, isLoadingMore } = useInfiniteLazyLoader<ManagementAccountCertificate>({
        pageSize: 8,
        scrollElement,
        contextKey: `earned-certificates:8`,
        loadPage: loadCertificatesPage,
        getItemKey: (certificate) => `${certificate.event_type}-${certificate.event_id}-${certificate.name}`,
        onError: (error) => {
            console.error('Error loading certificates:', error);
            handleError('Error loading certificates', error);
        },
    });

    const handleDownloadCertificates = async (certificate: ManagementAccountCertificate) => {
        const { event_id: eventId, event_type_slug: eventType, subscriber_id } = certificate;

        const itemPath = `management/events/${eventType}/${eventId}/subscribers/${subscriber_id}/certificate/download/`;
        try {
            setDownloadingFiles((prev) => new Set(prev).add(itemPath));
            await downloadCertificate({ relativePath: itemPath });
        } catch (error) {
            handleError('Error downloading certificate', error);
        } finally {
            setDownloadingFiles((prev) => {
                const newSet = new Set(prev);
                newSet.delete(itemPath);
                return newSet;
            });
        }
    };

    return (
        <Grid2 container columnSpacing="48px" rowSpacing="24px" py="24px">
            {hasRealItems || isLoadingMore ? (
                items.map((certificate) => {
                    if (isInfiniteLoadingItem(certificate)) {
                        return (
                            <Grid2 key={certificate.name} size={3}>
                                <MediaItemCard loading />
                            </Grid2>
                        );
                    }

                    return (
                        <Grid2 key={`certificate-${certificate.name}`} size={3}>
                            <MediaItemCard
                                key={certificate.name}
                                {...certificate}
                                thumbnail={CertificateImage}
                                file_type="pdf"
                                thumbnailProps={{ className: 'h-[154px] !w-fit mx-auto my-6', animated: false }}
                                onClick={() => {
                                    handleDownloadCertificates(certificate);
                                }}
                                downloadProps={{
                                    onClick: () => {
                                        handleDownloadCertificates(certificate);
                                    },
                                    loading: downloadingFiles.has(
                                        `management/events/${certificate.event_type_slug}/${certificate.event_id}/subscribers/${certificate.subscriber_id}/certificate/download/`
                                    ),
                                }}
                            />
                        </Grid2>
                    );
                })
            ) : isErrorFetchingCertificates ? (
                <CenteredEmptyState message="An error occurred while fetching this member's earned certificates." />
            ) : (
                <CenteredEmptyState message="This member hasn't acquired any certificates yet." />
            )}
            <div ref={sentinelRef} className="h-px basis-full" />
        </Grid2>
    );
};
